import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { EExportDownloadError, EExportJobType } from '~/features/exports/models'
import { downloadExportJob } from '~/features/exports/services/asyncExportApi'
import asyncExportJobsStore from '~/features/exports/store/asyncExportJobsStore'
import triggerPresignedDownload from '~/features/exports/utils/triggerPresignedDownload'

type TDownloadExportJobInput = {
  jobId: string
  type: EExportJobType
  downloadFilename?: string
}

function useDownloadExportJob() {
  const removeJob = asyncExportJobsStore((state) => state.removeJob)

  return useMutation({
    mutationFn: async ({
      jobId,
      type,
      downloadFilename,
    }: TDownloadExportJobInput) => {
      const result = await downloadExportJob(jobId, type)
      triggerPresignedDownload(
        result.url,
        result.downloadFilename ?? downloadFilename,
      )
      return jobId
    },
    onSuccess: (jobId) => {
      removeJob(jobId)
      toast.success('Download started')
    },
    onError: (error: Error, variables) => {
      if (error.message === EExportDownloadError.notReady) {
        toast.error(
          'Could not get a download link. Please try again in a moment.',
        )
        return
      }
      if (error.message === EExportDownloadError.expired) {
        removeJob(variables.jobId)
        toast.error('Export expired, please start a new one.')
        return
      }
      toast.error(
        error.message || 'Failed to download export. Please try again.',
      )
    },
  })
}

export default useDownloadExportJob
